"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import FileUploader from "./FileUploader";
import ProcessingOverlay from "./ProcessingOverlay";
import SignupGateModal from "./SignupGateModal";
import UsageLimitBanner from "./UsageLimitBanner";
import { useToolFetch } from "@/hooks/useToolFetch";
import { useAuth } from "@/lib/auth";
import type { SavedSignature } from "@/lib/types";
import {
  isMark,
  isGlyphLike,
  isMinimalBar,
  MODE_TO_MARK,
  GLYPH_LABEL,
  TOOLS,
  fieldChrome,
  GlyphIcon,
  DEFAULT_INK,
  INK_PRESETS,
  FONT_CSS,
  SIZE_PX,
  SIZE_LABEL,
  SIZE_H_NORM,
  pxToPreset,
  customPxHNorm,
  makeId,
  HISTORY_LIMIT,
  ZOOM_MIN,
  ZOOM_MAX,
  ZOOM_STEP,
  clampZoom,
} from "./fillAndSignShared";
import { LeftToolbar, MobileToolbar, PropertyBar } from "./fillAndSignChrome";
import type { InteractionMode, FontFamily, FontSize, PlacedField } from "./fillAndSignShared";

const SignatureModal = dynamic(
  () => import("@/components/signing/SignatureModal"),
  { ssr: false }
);

/* ─── Component ─── */
export default function FillAndSignTool() {
  /* File & PDF */
  const [file, setFile] = useState<File | null>(null);
  const [pages, setPages] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [zoom, setZoom] = useState(1); // 0.5–3.0 page zoom for precise placement

  /* Fields */
  const [fields, setFields] = useState<PlacedField[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  /* Interaction mode */
  const [mode, setMode] = useState<InteractionMode>("select");

  /* Signature modal */
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [pendingPlacement, setPendingPlacement] = useState<{ x: number; y: number } | null>(null);
  const [savedSignatures, setSavedSignatures] = useState<SavedSignature[]>([]);

  /* Processing / results */
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<{ url: string; size: number } | null>(null);
  const [error, setError] = useState("");

  /* Drag state */
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const dragStart = useRef<{ mx: number; my: number; ox: number; oy: number } | null>(null);
  const justDragged = useRef(false);
  const didMove = useRef(false);
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  /* Resize state */
  const [resizingId, setResizingId] = useState<string | null>(null);
  const resizeStart = useRef<
    { mx: number; my: number; ow: number; oh: number; ox: number; oy: number; aspect: number | null } | null
  >(null);
  const didResize = useRef(false);

  /* Whiteout draw state (drag a rectangle to cover content) */
  const [drawing, setDrawing] = useState(false);
  const [drawRect, setDrawRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const drawStartRef = useRef<{ x: number; y: number } | null>(null);
  const drawRectRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;

  /* Undo / redo — snapshots of `fields`. Refs mirror latest state so the
     handlers stay stable (no stale closures, no nested-updater double-fire). */
  const [past, setPast] = useState<PlacedField[][]>([]);
  const [future, setFuture] = useState<PlacedField[][]>([]);
  const gesturePrev = useRef<PlacedField[]>([]); // fields snapshot at drag/resize start
  const editPrevSnapshot = useRef<PlacedField[] | null>(null); // fields snapshot at text-edit start

  const fieldsRef = useRef(fields);
  const pastRef = useRef(past);
  const futureRef = useRef(future);
  fieldsRef.current = fields;
  pastRef.current = past;
  futureRef.current = future;

  /* Push the current fields onto the undo stack (call BEFORE a discrete mutation) */
  const snapshot = useCallback(() => {
    setPast((p) => [...p, fieldsRef.current].slice(-HISTORY_LIMIT));
    setFuture([]);
  }, []);

  const undo = useCallback(() => {
    const p = pastRef.current;
    if (p.length === 0) return;
    const prev = p[p.length - 1];
    setPast(p.slice(0, -1));
    setFuture([fieldsRef.current, ...futureRef.current].slice(0, HISTORY_LIMIT));
    setFields(prev);
    setSelectedId(null);
    setEditingId(null);
  }, []);

  const redo = useCallback(() => {
    const f = futureRef.current;
    if (f.length === 0) return;
    const next = f[0];
    setFuture(f.slice(1));
    setPast([...pastRef.current, fieldsRef.current].slice(-HISTORY_LIMIT));
    setFields(next);
    setSelectedId(null);
    setEditingId(null);
  }, []);

  const { toolFetch, quotaError } = useToolFetch();
  const { user } = useAuth();
  /* ─── Auto-scroll to top on result ─── */
  useEffect(() => {
    if (result) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [result]);

  /* ─── Fetch saved signatures ─── */
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("access_token");
    if (!token) return;
    fetch("/api/auth/me/signatures", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setSavedSignatures(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [user]);

  /* ─── Keyboard shortcuts ─── */
  useEffect(() => {
    if (!file || !pages.length) return;
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const key = e.key.toLowerCase();
      if (e.ctrlKey || e.metaKey) {
        // Undo/redo — and swallow other combos so e.g. Ctrl+C doesn't trigger the Check tool
        if (key === "z") { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
        else if (key === "y") { e.preventDefault(); redo(); }
        return;
      }
      const tool = TOOLS.find((t) => t.key.toLowerCase() === key);
      if (tool) {
        setMode((m) => (m === tool.mode ? "select" : tool.mode));
        return;
      }
      if (key === "+" || key === "=") { setZoom((z) => clampZoom(z + ZOOM_STEP)); return; }
      if (key === "-" || key === "_") { setZoom((z) => clampZoom(z - ZOOM_STEP)); return; }
      if (key === "delete" || key === "backspace") {
        if (selectedId && editingId !== selectedId) {
          e.preventDefault();
          snapshot();
          setFields((p) => p.filter((f) => f.id !== selectedId));
          setSelectedId(null);
        }
      } else if (key === "escape") {
        setSelectedId(null);
        setEditingId(null);
        setMode("select");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [file, pages.length, selectedId, editingId]);

  /* ─── File handling ─── */
  const handleFilesSelected = useCallback((files: File[]) => {
    setFile(files[0] || null);
    setPages([]);
    setFields([]);
    setPast([]);
    setFuture([]);
    setZoom(1);
    setResult(null);
    setError("");
    setSelectedId(null);
    setEditingId(null);
    setMode("select");
  }, []);

  /* ─── Render PDF pages ─── */
  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    setLoadingPdf(true);

    const render = async () => {
      try {
        const pdfjsLib = await import("pdfjs-dist/legacy/build/pdf.mjs");
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
          import.meta.url
        ).toString();
        const data = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data }).promise;
        const imgs: string[] = [];
        for (let i = 0; i < pdf.numPages; i++) {
          const page = await pdf.getPage(i + 1);
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: canvas.getContext("2d")!, viewport, canvas }).promise;
          imgs.push(canvas.toDataURL("image/jpeg", 0.85));
          if (cancelled) return;
        }
        setPages(imgs);
      } catch {
        setError("Failed to load PDF");
      } finally {
        if (!cancelled) setLoadingPdf(false);
      }
    };
    render();
    return () => { cancelled = true; };
  }, [file]);

  /* ─── Click on PDF page ─── */
  const handlePageClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (justDragged.current) { justDragged.current = false; return; }

      const rect = e.currentTarget.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      if (mode === "select") {
        setSelectedId(null);
        setEditingId(null);
        return;
      }

      if (mode === "add-signature") {
        setPendingPlacement({ x, y });
        setShowSignatureModal(true);
        return;
      }

      const markType = MODE_TO_MARK[mode];
      if (markType) {
        snapshot();
        const id = makeId();
        const wNorm = 0.035;
        const hNorm = wNorm * (rect.width / rect.height); // visually square (px)
        setFields((prev) => [
          ...prev,
          {
            id,
            type: markType,
            pageIndex: currentPage,
            xNorm: Math.max(0, Math.min(1 - wNorm, x - wNorm / 2)),
            yNorm: Math.max(0, Math.min(1 - hNorm, y - hNorm / 2)),
            wNorm,
            hNorm,
            value: "",
            font: "sans",
            size: "medium",
          },
        ]);
        setSelectedId(id);
        setMode("select");
        return;
      }

      if (mode === "add-date") {
        snapshot();
        const today = new Date().toLocaleDateString("en-US");
        const id = makeId();
        setFields((prev) => [
          ...prev,
          { id, type: "date", pageIndex: currentPage, xNorm: Math.max(0, x - 0.07), yNorm: Math.max(0, y - 0.015), wNorm: 0.14, hNorm: SIZE_H_NORM.medium, value: today, font: "sans", size: "medium" },
        ]);
        setSelectedId(id);
        setMode("select");
        return;
      }

      if (mode === "add-text") {
        snapshot();
        const id = makeId();
        setFields((prev) => [
          ...prev,
          { id, type: "text", pageIndex: currentPage, xNorm: Math.max(0, x - 0.1), yNorm: Math.max(0, y - 0.015), wNorm: 0.2, hNorm: SIZE_H_NORM.medium, value: "", font: "sans", size: "medium" },
        ]);
        setSelectedId(id);
        setEditingId(id);
        setMode("select");
        return;
      }
    },
    [mode, currentPage]
  );

  /* ─── Finish text editing ─── */
  const finishEdit = useCallback(
    (id: string) => {
      // Record one undo step per edit session that actually changed the value
      const snap = editPrevSnapshot.current;
      if (snap) {
        const before = snap.find((f) => f.id === id)?.value ?? "";
        const after = fieldsRef.current.find((f) => f.id === id)?.value ?? "";
        if (before !== after) {
          setPast((p) => [...p, snap].slice(-HISTORY_LIMIT));
          setFuture([]);
        }
        editPrevSnapshot.current = null;
      }
      setFields((prev) => prev.filter((f) => f.id !== id || f.value.trim() !== ""));
      setEditingId(null);
    },
    []
  );

  /* ─── Apply signature from modal ─── */
  const handleSignatureApply = useCallback(
    (dataUrl: string) => {
      if (!pendingPlacement) return;
      snapshot();
      const id = makeId();
      setFields((prev) => [
        ...prev,
        {
          id,
          type: "signature",
          pageIndex: currentPage,
          xNorm: Math.max(0, pendingPlacement.x - 0.1),
          yNorm: Math.max(0, pendingPlacement.y - 0.03),
          wNorm: 0.2,
          hNorm: 0.06,
          value: "",
          signatureDataUrl: dataUrl,
          font: "sans",
          size: "medium",
        },
      ]);
      setSelectedId(id);
      setShowSignatureModal(false);
      setPendingPlacement(null);
      setMode("select");
    },
    [pendingPlacement, currentPage]
  );

  const handleDeleteSavedSig = useCallback(
    (sigId: string) => {
      const token = localStorage.getItem("access_token");
      if (!token) return;
      fetch(`/api/auth/me/signatures/${sigId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } })
        .then((r) => { if (r.ok) setSavedSignatures((prev) => prev.filter((s) => s.id !== sigId)); });
    },
    []
  );

  /* ─── Drag handlers ─── */
  const handleDragStart = useCallback(
    (e: React.PointerEvent, id: string) => {
      e.stopPropagation();
      const f = fields.find((f) => f.id === id);
      if (!f) return;
      dragStart.current = { mx: e.clientX, my: e.clientY, ox: f.xNorm, oy: f.yNorm };
      didMove.current = false;
      gesturePrev.current = fieldsRef.current;
      setDraggingId(id);
    },
    [fields]
  );

  useEffect(() => {
    if (draggingId === null) return;
    const container = pdfContainerRef.current;
    if (!container) return;

    const handlePointerMove = (e: PointerEvent) => {
      if (!dragStart.current) return;
      const dist = Math.abs(e.clientX - dragStart.current.mx) + Math.abs(e.clientY - dragStart.current.my);
      if (dist < 4) return; // Ignore tiny movements (click, not drag)
      if (!didMove.current) {
        setPast((p) => [...p, gesturePrev.current].slice(-HISTORY_LIMIT));
        setFuture([]);
      }
      didMove.current = true;
      const rect = container.getBoundingClientRect();
      const dx = (e.clientX - dragStart.current.mx) / rect.width;
      const dy = (e.clientY - dragStart.current.my) / rect.height;
      const field = fields.find((f) => f.id === draggingId);
      if (!field) return;
      const newX = Math.max(0, Math.min(1 - field.wNorm, dragStart.current.ox + dx));
      const newY = Math.max(0, Math.min(1 - field.hNorm, dragStart.current.oy + dy));
      setFields((prev) =>
        prev.map((f) => (f.id === draggingId ? { ...f, xNorm: newX, yNorm: newY } : f))
      );
    };

    const handlePointerUp = () => {
      dragStart.current = null;
      setDraggingId(null);
      if (didMove.current) {
        justDragged.current = true;
      }
      didMove.current = false;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [draggingId, fields]);

  /* ─── Resize handlers (corner handle; aspect-locked for signatures) ─── */
  const handleResizeStart = useCallback(
    (e: React.PointerEvent, id: string) => {
      e.stopPropagation();
      e.preventDefault();
      const f = fields.find((f) => f.id === id);
      if (!f) return;
      resizeStart.current = {
        mx: e.clientX,
        my: e.clientY,
        ow: f.wNorm,
        oh: f.hNorm,
        ox: f.xNorm,
        oy: f.yNorm,
        aspect: isGlyphLike(f.type) ? f.wNorm / Math.max(f.hNorm, 0.0001) : null,
      };
      didResize.current = false;
      gesturePrev.current = fieldsRef.current;
      setSelectedId(id);
      setResizingId(id);
    },
    [fields]
  );

  useEffect(() => {
    if (resizingId === null) return;
    const container = pdfContainerRef.current;
    if (!container) return;
    const MIN_W = 0.03;
    const MIN_H = 0.015;

    const handlePointerMove = (e: PointerEvent) => {
      const rs = resizeStart.current;
      if (!rs) return;
      const dist = Math.abs(e.clientX - rs.mx) + Math.abs(e.clientY - rs.my);
      if (dist >= 4) {
        if (!didResize.current) {
          setPast((p) => [...p, gesturePrev.current].slice(-HISTORY_LIMIT));
          setFuture([]);
        }
        didResize.current = true;
      }
      const rect = container.getBoundingClientRect();
      const dx = (e.clientX - rs.mx) / rect.width;
      const dy = (e.clientY - rs.my) / rect.height;
      const maxW = 1 - rs.ox;
      const maxH = 1 - rs.oy;
      let newW: number;
      let newH: number;
      if (rs.aspect) {
        // Uniform scale, kept inside page bounds
        newW = Math.max(MIN_W, rs.ow + dx);
        newW = Math.min(newW, maxW, maxH * rs.aspect);
        newH = newW / rs.aspect;
      } else {
        newW = Math.min(Math.max(MIN_W, rs.ow + dx), maxW);
        newH = Math.min(Math.max(MIN_H, rs.oh + dy), maxH);
      }
      setFields((prev) =>
        prev.map((f) => (f.id === resizingId ? { ...f, wNorm: newW, hNorm: newH } : f))
      );
    };

    const handlePointerUp = () => {
      resizeStart.current = null;
      setResizingId(null);
      if (didResize.current) justDragged.current = true; // swallow the trailing click (no deselect)
      didResize.current = false;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [resizingId]);

  /* ─── Whiteout draw handlers (drag a rectangle on the page) ─── */
  const handleWhiteoutStart = useCallback((e: React.PointerEvent) => {
    if (mode !== "add-whiteout") return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    drawStartRef.current = { x, y };
    drawRectRef.current = { x, y, w: 0, h: 0 };
    setDrawRect({ x, y, w: 0, h: 0 });
    setDrawing(true);
  }, [mode]);

  useEffect(() => {
    if (!drawing) return;
    const container = pdfContainerRef.current;
    if (!container) return;

    const handlePointerMove = (e: PointerEvent) => {
      const s = drawStartRef.current;
      if (!s) return;
      const rect = container.getBoundingClientRect();
      const cx = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      const cy = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
      const r = { x: Math.min(s.x, cx), y: Math.min(s.y, cy), w: Math.abs(cx - s.x), h: Math.abs(cy - s.y) };
      drawRectRef.current = r;
      setDrawRect(r);
    };

    const handlePointerUp = () => {
      const r = drawRectRef.current;
      setDrawing(false);
      setDrawRect(null);
      drawStartRef.current = null;
      drawRectRef.current = null;
      if (r && r.w > 0.01 && r.h > 0.01) {
        snapshot();
        const id = makeId();
        setFields((prev) => [
          ...prev,
          { id, type: "whiteout", pageIndex: currentPageRef.current, xNorm: r.x, yNorm: r.y, wNorm: r.w, hNorm: r.h, value: "", font: "sans", size: "medium" },
        ]);
        setSelectedId(id);
        justDragged.current = true; // swallow the trailing click
      }
      setMode("select");
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [drawing, snapshot]);

  /* ─── Submit to backend ─── */
  const submit = useCallback(async () => {
    if (!file || fields.length === 0) {
      setError("Add at least one field to the document");
      return;
    }
    setProcessing(true);
    setError("");

    try {
      const backendFields = fields.map((f) => ({
        page: f.pageIndex,
        x: f.xNorm,
        y: f.yNorm,
        w: f.wNorm,
        h: f.hNorm,
        type: f.type,
        value: f.value || undefined,
        imageData: f.signatureDataUrl ? f.signatureDataUrl.replace(/^data:[^;]+;base64,/, "") : undefined,
        font: f.font,
        size: f.customPx ? undefined : f.size,
        fontsize: f.customPx || undefined, // Custom px sent directly
        color: f.color || DEFAULT_INK,
      }));

      const formData = new FormData();
      formData.append("file", file);
      formData.append("fields", JSON.stringify(backendFields));

      const res = await toolFetch("/api/tools/fill-sign", { method: "POST", body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to fill and sign PDF");
      }

      const blob = await res.blob();
      setResult({ url: URL.createObjectURL(blob), size: blob.size });
    } catch (e: any) {
      setError(e.message || "Failed to fill and sign PDF");
    } finally {
      setProcessing(false);
    }
  }, [file, fields]);

  const reset = useCallback(() => {
    setFile(null);
    setPages([]);
    setFields([]);
    setPast([]);
    setFuture([]);
    setZoom(1);
    setResult(null);
    setError("");
    setSelectedId(null);
    setEditingId(null);
    setMode("select");
  }, []);

  /* ─── Update field property ─── */
  const updateField = useCallback((id: string, updates: Partial<PlacedField>) => {
    setFields((prev) => prev.map((f) => {
      if (f.id !== id) return f;
      const merged = { ...f, ...updates };
      // Auto-adjust height when size changes for text/date fields
      if (updates.size && f.type !== "signature") {
        merged.hNorm = SIZE_H_NORM[updates.size];
        merged.customPx = undefined; // Clear custom when preset selected
      }
      if (updates.customPx && f.type !== "signature") {
        merged.hNorm = customPxHNorm(updates.customPx);
        merged.size = pxToPreset(updates.customPx);
      }
      return merged;
    }));
  }, []);

  /* ─── Result screen with CTAs ─── */
  if (result) {
    const sizeMB = (result.size / 1024 / 1024).toFixed(2);
    return (
      <div className="flex flex-col items-center gap-6 py-8">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
          <svg className="h-8 w-8 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </div>
        <div className="text-center">
          <h3 className="text-lg font-bold text-slate-900">Document Ready</h3>
          <p className="mt-1 text-sm text-slate-500">filled-signed.pdf <span className="text-slate-400">({sizeMB} MB)</span></p>
        </div>
        <a href={result.url} download="filled-signed.pdf" className="btn-primary px-8 py-3 text-base">
          <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
          Download PDF
        </a>
        <button onClick={reset} className="text-sm text-slate-500 hover:text-slate-700 underline underline-offset-2 transition-colors">
          Fill another document
        </button>

        {/* Post-processing CTAs */}
        <div className="mt-2 w-full max-w-lg space-y-3">
          <Link href={user ? "/envelopes/new" : "/register"} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-300 hover:shadow-sm transition-all group">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-900">Send for e-signature</p>
              <p className="text-xs text-slate-500">Need someone else to sign? Send it with a legally binding link.</p>
            </div>
            <svg className="h-4 w-4 text-slate-400 group-hover:text-slate-600 transition-colors" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </Link>

          <Link href={user ? "/dashboard/locker" : "/register"} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-300 hover:shadow-sm transition-all group">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-900">Store in Secure Locker</p>
              <p className="text-xs text-slate-500">Keep your signed documents safe and organized.</p>
            </div>
            <svg className="h-4 w-4 text-slate-400 group-hover:text-slate-600 transition-colors" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </Link>

          {!user && (
            <Link href="/register" className="flex items-center gap-4 rounded-xl border border-[var(--color-gold)] bg-[var(--color-gold-muted)]/30 p-4 hover:shadow-sm transition-all group">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--color-gold-muted)] text-[var(--color-gold-dark)]">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900">Create a free account</p>
                <p className="text-xs text-slate-600">50 tool uses/day, saved signatures, 10 e-signature envelopes/mo.</p>
              </div>
              <svg className="h-4 w-4 text-slate-400 group-hover:text-slate-600 transition-colors" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </Link>
          )}
        </div>

      </div>
    );
  }

  if (quotaError?.gate === "signup") return <SignupGateModal quotaError={quotaError} />;

  if (!file) {
    return (
      <div className="space-y-6">
        {quotaError?.gate === "limit" && <UsageLimitBanner quotaError={quotaError} />}
        <FileUploader accept=".pdf" onFilesSelected={handleFilesSelected} label="Drop your PDF here" description="Fill out forms and add your signature" />
      </div>
    );
  }

  if (loadingPdf) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <div className="relative h-8 w-8">
            <div className="absolute inset-0 rounded-full border-2 border-slate-200" />
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-brand-600 animate-spin" />
          </div>
          <p className="text-sm text-slate-500">Loading PDF...</p>
        </div>
      </div>
    );
  }

  const selectedField = fields.find((f) => f.id === selectedId);
  const currentPageFields = fields.filter((f) => f.pageIndex === currentPage);

  /* Ink color swatches (text/date/marks) — rendered in the property bar */
  const renderColorPicker = (field: PlacedField) => (
    <div className="flex items-center gap-1 rounded-lg bg-white/10 px-2 py-1">
      {INK_PRESETS.map((c) => (
        <button
          key={c}
          onClick={() => { snapshot(); updateField(field.id, { color: c }); }}
          className={`h-4 w-4 rounded-full transition-transform ${
            (field.color || DEFAULT_INK).toLowerCase() === c ? "ring-2 ring-white scale-110" : "ring-1 ring-white/40 hover:scale-110"
          }`}
          style={{ backgroundColor: c }}
          title={c}
          aria-label={`Ink ${c}`}
        />
      ))}
      <label
        onPointerDown={() => snapshot()}
        className="relative h-4 w-4 cursor-pointer overflow-hidden rounded-full ring-1 ring-white/40"
        title="Custom color"
        style={{ background: "conic-gradient(from 0deg, #ef4444, #eab308, #22c55e, #3b82f6, #a855f7, #ef4444)" }}
      >
        <input
          type="color"
          value={field.color || DEFAULT_INK}
          onChange={(e) => updateField(field.id, { color: e.target.value })}
          className="absolute -inset-1 h-6 w-6 cursor-pointer opacity-0"
        />
      </label>
    </div>
  );

  const modeHints: Record<InteractionMode, string> = {
    select: "Click a field to select it, drag to move",
    "add-text": "Click on the document to place a text field",
    "add-date": "Click to place today\u2019s date",
    "add-signature": "Click to place a signature",
    "add-check": "Click to place a checkmark",
    "add-cross": "Click to place a cross (\u2717)",
    "add-dot": "Click to place a dot",
    "add-whiteout": "Drag a rectangle to white-out content",
  };

  const cursorClass: Record<InteractionMode, string> = {
    select: "cursor-default",
    "add-text": "cursor-crosshair",
    "add-date": "cursor-crosshair",
    "add-signature": "cursor-crosshair",
    "add-check": "cursor-crosshair",
    "add-cross": "cursor-crosshair",
    "add-dot": "cursor-crosshair",
    "add-whiteout": "cursor-crosshair",
  };

  return (
    <div className="relative">
      {processing && <ProcessingOverlay message="Processing your document..." />}

      {/* Signature modal */}
      {showSignatureModal && (
        <SignatureModal
          signerName=""
          onApply={handleSignatureApply}
          onClose={() => { setShowSignatureModal(false); setPendingPlacement(null); }}
          savedSignatures={savedSignatures.length > 0 ? savedSignatures : undefined}
          onDeleteSaved={savedSignatures.length > 0 ? handleDeleteSavedSig : undefined}
        />
      )}

      <div className="flex gap-3">
        {/* ─── Left Toolbar (sticky) ─── */}
        <LeftToolbar
          mode={mode}
          setMode={setMode}
          fields={fields}
          past={past}
          future={future}
          undo={undo}
          redo={redo}
          reset={reset}
          submit={submit}
          processing={processing}
        />

        {/* ─── Main content ─── */}
        <div className="flex-1 min-w-0 space-y-3">
          {/* Mobile toolbar (horizontal, icon-only — 7 tools) */}
          <MobileToolbar
            mode={mode}
            setMode={setMode}
            past={past}
            future={future}
            undo={undo}
            redo={redo}
          />

          {/* Sticky top bar: mode hint + property bar */}
          <PropertyBar
            mode={mode}
            modeHints={modeHints}
            selectedField={selectedField}
            setFields={setFields}
            setSelectedId={setSelectedId}
            setEditingId={setEditingId}
            updateField={updateField}
            snapshot={snapshot}
            renderColorPicker={renderColorPicker}
          />

          {/* PDF preview with field overlays */}
          {pages.length > 0 && (
            <div>
              {/* Zoom + page controls */}
              <div className="flex items-center justify-between gap-2 mb-2">
                {pages.length > 1 ? (
                  <div className="flex items-center gap-2">
                    <button onClick={() => setCurrentPage(Math.max(0, currentPage - 1))} disabled={currentPage === 0} className="btn-secondary py-1 px-2 text-xs">Prev</button>
                    <span className="text-xs text-slate-500 tabular-nums">Page {currentPage + 1} / {pages.length}</span>
                    <button onClick={() => setCurrentPage(Math.min(pages.length - 1, currentPage + 1))} disabled={currentPage === pages.length - 1} className="btn-secondary py-1 px-2 text-xs">Next</button>
                  </div>
                ) : <span />}

                <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5">
                  <button
                    onClick={() => setZoom((z) => clampZoom(z - ZOOM_STEP))}
                    disabled={zoom <= ZOOM_MIN}
                    className="flex h-6 w-6 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent"
                    title="Zoom out (−)"
                    aria-label="Zoom out"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" /></svg>
                  </button>
                  <button
                    onClick={() => setZoom(1)}
                    className="min-w-[3rem] rounded px-1 text-xs font-medium tabular-nums text-slate-600 hover:bg-slate-100"
                    title="Reset zoom"
                  >
                    {Math.round(zoom * 100)}%
                  </button>
                  <button
                    onClick={() => setZoom((z) => clampZoom(z + ZOOM_STEP))}
                    disabled={zoom >= ZOOM_MAX}
                    className="flex h-6 w-6 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent"
                    title="Zoom in (+)"
                    aria-label="Zoom in"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" /></svg>
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
              <div
                ref={pdfContainerRef}
                className={`relative mx-auto rounded-lg border border-slate-200 overflow-hidden bg-slate-100 ${cursorClass[mode]}`}
                style={{ touchAction: mode === "add-whiteout" ? "none" : undefined, width: `${zoom * 100}%` }}
                onClick={handlePageClick}
                onPointerDown={handleWhiteoutStart}
              >
                <img
                  src={pages[currentPage]}
                  alt={`Page ${currentPage + 1}`}
                  className="w-full select-none pointer-events-none"
                  draggable={false}
                />

                {/* Field overlays */}
                {currentPageFields.map((f) => {
                  const isSelected = f.id === selectedId;
                  const isEditing = f.id === editingId;
                  const isDragging = f.id === draggingId;
                  const isResizing = f.id === resizingId;

                  return (
                    <div
                      key={f.id}
                      className={`absolute transition-shadow ${
                        isDragging ? "opacity-80 z-30" : isResizing ? "z-30" : isSelected ? "z-20" : "z-10"
                      } ${mode === "select" ? "cursor-move" : ""}`}
                      style={{
                        left: `${f.xNorm * 100}%`,
                        top: `${f.yNorm * 100}%`,
                        width: `${f.wNorm * 100}%`,
                        height: `${f.hNorm * 100}%`,
                        touchAction: mode === "select" ? "none" : undefined,
                      }}
                      onPointerDown={(e) => {
                        if (mode !== "select") return;
                        handleDragStart(e, f.id);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (justDragged.current) { justDragged.current = false; return; }
                        if (mode !== "select") return;
                        setSelectedId(f.id);
                        if (f.type === "text") { setEditingId(f.id); editPrevSnapshot.current = fieldsRef.current; }
                      }}
                    >
                      {/* Field background */}
                      <div className={`absolute inset-0 rounded border ${fieldChrome(f.type, isSelected, isEditing)}`} />

                      {/* Field content */}
                      {f.type === "signature" && f.signatureDataUrl && (
                        <img
                          src={f.signatureDataUrl}
                          alt="Signature"
                          className="absolute inset-0 w-full h-full object-contain p-0.5 pointer-events-none"
                          draggable={false}
                        />
                      )}

                      {isMark(f.type) && (
                        <GlyphIcon
                          type={f.type}
                          className="absolute inset-0 w-full h-full p-0.5 pointer-events-none"
                          style={{ color: f.color || DEFAULT_INK }}
                        />
                      )}

                      {f.type === "text" && isEditing && (
                        <input
                          autoFocus
                          type="text"
                          value={f.value}
                          onChange={(e) => updateField(f.id, { value: e.target.value })}
                          onBlur={() => finishEdit(f.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") finishEdit(f.id);
                            if (e.key === "Escape") { setEditingId(null); setSelectedId(null); }
                            e.stopPropagation();
                          }}
                          className="relative w-full h-full px-1 bg-transparent outline-none z-10"
                          style={{
                            fontFamily: FONT_CSS[f.font],
                            fontSize: `${f.customPx || SIZE_PX[f.size]}px`,
                            lineHeight: 1,
                            color: f.color || DEFAULT_INK,
                          }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}

                      {f.type === "text" && !isEditing && f.value && (
                        <div
                          className="relative flex items-center w-full h-full px-1 truncate z-10"
                          style={{
                            fontFamily: FONT_CSS[f.font],
                            fontSize: `${f.customPx || SIZE_PX[f.size]}px`,
                            lineHeight: 1,
                            color: f.color || DEFAULT_INK,
                          }}
                        >
                          {f.value}
                        </div>
                      )}

                      {f.type === "date" && (
                        <div
                          className="relative flex items-center w-full h-full px-1 truncate z-10"
                          style={{
                            fontFamily: FONT_CSS[f.font],
                            fontSize: `${f.customPx || SIZE_PX[f.size]}px`,
                            lineHeight: 1,
                            color: f.color || DEFAULT_INK,
                          }}
                        >
                          {f.value}
                        </div>
                      )}

                      {/* Delete button — visible when selected */}
                      {isSelected && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            snapshot();
                            setFields((p) => p.filter((ff) => ff.id !== f.id));
                            setSelectedId(null);
                            setEditingId(null);
                          }}
                          onPointerDown={(e) => e.stopPropagation()}
                          className="absolute -top-2.5 -right-2.5 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center text-[10px] hover:bg-red-600 shadow-sm z-30"
                        >
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}

                      {/* Resize handle (bottom-right) — visible when selected in select mode */}
                      {isSelected && mode === "select" && (
                        <div
                          onPointerDown={(e) => handleResizeStart(e, f.id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ touchAction: "none" }}
                          className="absolute -bottom-1.5 -right-1.5 h-3.5 w-3.5 rounded-sm border-2 border-blue-500 bg-white cursor-se-resize z-30 shadow-sm"
                          title="Drag to resize"
                        />
                      )}
                    </div>
                  );
                })}

                {/* Whiteout draw preview */}
                {drawRect && (drawRect.w > 0 || drawRect.h > 0) && (
                  <div
                    className="absolute border-2 border-dashed border-slate-400 bg-white/70 pointer-events-none z-40"
                    style={{
                      left: `${drawRect.x * 100}%`,
                      top: `${drawRect.y * 100}%`,
                      width: `${drawRect.w * 100}%`,
                      height: `${drawRect.h * 100}%`,
                    }}
                  />
                )}
              </div>
              </div>

              {/* Bottom page nav */}
              {pages.length > 1 && (
                <div className="flex items-center justify-center gap-2 mt-2">
                  <button onClick={() => setCurrentPage(Math.max(0, currentPage - 1))} disabled={currentPage === 0} className="btn-secondary py-1 px-2 text-xs">Prev</button>
                  <span className="text-xs text-slate-500 tabular-nums">Page {currentPage + 1} / {pages.length}</span>
                  <button onClick={() => setCurrentPage(Math.min(pages.length - 1, currentPage + 1))} disabled={currentPage === pages.length - 1} className="btn-secondary py-1 px-2 text-xs">Next</button>
                </div>
              )}
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          {/* Action bar */}
          {fields.length > 0 ? (
            <div className="flex flex-col sm:flex-row items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex-1 text-center sm:text-left">
                <p className="text-sm font-medium text-slate-700">
                  {fields.length} field{fields.length !== 1 ? "s" : ""} added to your document
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  All changes are applied when you download
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={reset}
                  className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
                >
                  Start over
                </button>
                <button
                  onClick={submit}
                  disabled={processing}
                  className="flex items-center gap-2 rounded-lg bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Download Filled PDF
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>Use the toolbar to add text, dates, or signatures</span>
              <span className="hidden sm:inline">Press V / T / D / S to switch tools</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
